import React, { useCallback, useMemo } from 'react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { AnimatePresence } from 'framer-motion';
import { useDossier } from './DossierContext';
import { useBlocksManagement } from './hooks/useBlocksManagement';
import { useVoiceDictation } from '../components/hooks/useVoiceDictation';
import WidgetRenderer from '../components/widgets/WidgetRenderer';
import A4PageWrapper from '../components/A4PageWrapper';
import { SortableBlock } from '../components/SortableBlock';
import PlaceholderBlock from '../components/PlaceholderBlock';
import AddWidgetButton from '../components/AddWidgetButton';
import VersionPreviewBanner from '../components/VersionPreviewBanner';
import PostItManager from '../components/postits/PostItManager';
import MacadamiaSwitcher from '../components/MacadamiaSwitcher';
import { useViewport } from '../components/hooks/useViewport';
import { ISection } from '../components/widgets/types';
import '../styles/LegalTypography.css';

/**
 * Zone centrale de l'éditeur — le contenu A4 + les widgets.
 *
 * Toute la lecture du dossier passe par `useDossier()`. Les opérations
 * sur les blocs viennent de `useBlocksManagement()`. La dictée vocale
 * et la barre flottante mobile restent ici car elles touchent au
 * rendu central.
 */
interface DossierEditorPaneProps {
  /** Ref vers le conteneur scrollable (consommée par PostItManager). */
  editorContainerRef: React.RefObject<HTMLDivElement>;
}

const DossierEditorPane: React.FC<DossierEditorPaneProps> = ({ editorContainerRef }) => {
  const {
    dossier,
    updateDossier,
    currentChapter,
    currentChapterIndex,
    isEditing,
    isPreviewMode,
    previewVersion,
    exitPreview,
    restoreFromPreview,
    isRestoringPreview,
    suggestions,
    acceptSuggestion,
    rejectSuggestion,
    addSuggestion,
    dossierId,
    userRole,
  } = useDossier();
  const { isMobile } = useViewport();

  const {
    changeBlockLocal,
    duplicateBlock,
    deleteBlock,
    addWidget,
    addWidgetAfter,
  } = useBlocksManagement();

  const { isRecording, isTranscribing, startRecording, stopAndTranscribe } = useVoiceDictation();

  const handleSuggestionCreated = useCallback(
    (s: Parameters<typeof addSuggestion>[0]) => {
      addSuggestion(s);
    },
    [addSuggestion]
  );

  const handleAddCompositeTextAndFocus = useCallback(() => {
    const newBlockId = addWidget('CompositeTextWidget');
    if (!newBlockId) return;
    setTimeout(() => {
      const blockElement = document.querySelector(`[data-block-id="${newBlockId}"]`);
      if (blockElement) {
        const editableDiv = blockElement.querySelector('[contenteditable="true"]');
        if (editableDiv instanceof HTMLElement) editableDiv.focus();
      }
    }, 100);
  }, [addWidget]);

  const handleDictation = useCallback(async () => {
    if (isRecording) {
      try {
        const text = await stopAndTranscribe();
        if (!text) return;
        const newBlockId = addWidget('CompositeTextWidget');
        if (!newBlockId) return;
        // L'injection du texte dicté doit passer par updateDossier
        // (Immer) — le state est frozen en dehors d'un recipe.
        // Le setTimeout 50 ms laisse le temps au bloc fraîchement
        // créé d'être committé avant qu'on tente de le retrouver.
        setTimeout(() => {
          updateDossier(draft => {
            const chapter = draft.content[currentChapterIndex];
            if (!chapter) return;
            for (const section of (chapter.sections ?? [])) {
              const block = (section.blocks ?? []).find(b => b._id === newBlockId);
              if (block && block.type === 'compositeText') {
                const composite = block as any;
                if (composite.content?.blocks?.length > 0) {
                  composite.content.blocks[0].rawText = text;
                }
                return;
              }
            }
            console.warn('DossierEditorPane: bloc dictée introuvable', newBlockId);
          });
        }, 50);
      } catch (err) {
        console.error('Erreur dictée:', err);
      }
    } else {
      try {
        await startRecording();
      } catch (err) {
        console.error('Erreur démarrage dictée:', err);
      }
    }
  }, [isRecording, stopAndTranscribe, startRecording, addWidget, updateDossier, currentChapterIndex]);

  // Numérotation des paragraphes : calcul du counter-reset pour la
  // continuité entre chapitres (sauf si l'utilisateur a explicitement
  // demandé un reset par chapitre dans les settings).
  const counterResetStyle = useMemo(() => {
    if (dossier.settings?.resetParagraphNumbering || currentChapterIndex === 0) {
      return { counterReset: 'legal-paragraph' };
    }
    const previousParagraphs = dossier.content.slice(0, currentChapterIndex).reduce((acc, chapter) => {
      const chapterParagraphs = (chapter.sections ?? []).reduce((sectAcc: number, section: ISection) => {
        const sectionParagraphs = section.blocks
          ? section.blocks.filter((b: any) => b.type === 'paragraph').length
          : 0;
        return sectAcc + sectionParagraphs;
      }, 0);
      return acc + chapterParagraphs;
    }, 0);
    return { counterReset: `legal-paragraph ${previousParagraphs}` };
  }, [dossier, currentChapterIndex]);

  // Rendu des widgets pour le chapitre courant.
  const widgetsContent = useMemo(() => {
    if (!currentChapter) return null;

    const buildHandlers = (sectionIndex: number, blockIndex: number, blockId: string) => ({
      onChange: (newBlock: any) => changeBlockLocal(sectionIndex, blockIndex, newBlock, blockId),
      onDuplicate: () => duplicateBlock(sectionIndex, blockIndex, blockId),
      onDelete: () => deleteBlock(sectionIndex, blockIndex, blockId),
      onInsertAfter: (widgetType: string) => addWidgetAfter(sectionIndex, blockIndex, widgetType, blockId),
    });

    if (isEditing) {
      const allBlocks: Array<{ block: any; sectionIndex: number; blockIndex: number }> = [];
      (currentChapter.sections ?? []).forEach((section: ISection, sectionIndex: number) => {
        (section.blocks ?? []).forEach((block: any, blockIndex: number) => {
          allBlocks.push({ block, sectionIndex, blockIndex });
        });
      });
      const blockIds = allBlocks.map(item => item.block._id);

      return (
        <SortableContext items={blockIds} strategy={verticalListSortingStrategy}>
          <div>
            {allBlocks.map(({ block, sectionIndex, blockIndex }) => {
              const handlers = buildHandlers(sectionIndex, blockIndex, block._id);
              const blockSuggestions = suggestions.filter(s => s.blocId === block._id);
              return (
                <SortableBlock
                  key={block._id}
                  id={block._id}
                  sectionIndex={sectionIndex}
                  blockIndex={blockIndex}
                  block={block}
                >
                  <WidgetRenderer
                    block={block}
                    onChange={handlers.onChange}
                    onDuplicate={handlers.onDuplicate}
                    onDelete={handlers.onDelete}
                    onInsertAfter={handlers.onInsertAfter}
                    suggestions={blockSuggestions}
                    onAcceptSuggestion={acceptSuggestion}
                    onRejectSuggestion={rejectSuggestion}
                    dossierId={dossierId}
                    onSuggestionCreated={handleSuggestionCreated}
                    userRole={userRole}
                    isFirstBlock={sectionIndex === 0 && blockIndex === 0}
                    chapterIndex={currentChapterIndex}
                    chapterTitle={currentChapter?.title || `Chapitre ${currentChapterIndex + 1}`}
                    dossierContent={block.type === 'summary' ? dossier : undefined}
                  />
                </SortableBlock>
              );
            })}
            <PlaceholderBlock
              onCreateBlock={handleAddCompositeTextAndFocus}
              onStartDictation={handleDictation}
              isDictating={isRecording}
              isDictationTranscribing={isTranscribing}
            />
          </div>
        </SortableContext>
      );
    }

    return (
      <div>
        {(currentChapter.sections ?? []).map((section: ISection, sectionIndex: number) => (
          <div key={sectionIndex}>
            {(section.blocks ?? []).map((block: any, blockIndex: number) => {
              const handlers = buildHandlers(sectionIndex, blockIndex, block._id);
              const blockSuggestions = suggestions.filter(s => s.blocId === block._id);
              return (
                <div key={block._id}>
                  <WidgetRenderer
                    block={block}
                    onChange={handlers.onChange}
                    onDuplicate={handlers.onDuplicate}
                    onDelete={handlers.onDelete}
                    onInsertAfter={handlers.onInsertAfter}
                    suggestions={blockSuggestions}
                    onAcceptSuggestion={acceptSuggestion}
                    onRejectSuggestion={rejectSuggestion}
                    dossierId={dossierId}
                    onSuggestionCreated={handleSuggestionCreated}
                    userRole={userRole}
                    isFirstBlock={sectionIndex === 0 && blockIndex === 0}
                    chapterIndex={currentChapterIndex}
                    chapterTitle={currentChapter?.title || `Chapitre ${currentChapterIndex + 1}`}
                    dossierContent={block.type === 'summary' ? dossier : undefined}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }, [
    currentChapter,
    isEditing,
    suggestions,
    changeBlockLocal,
    duplicateBlock,
    deleteBlock,
    addWidgetAfter,
    acceptSuggestion,
    rejectSuggestion,
    dossierId,
    handleSuggestionCreated,
    userRole,
    currentChapterIndex,
    dossier,
    handleAddCompositeTextAndFocus,
    handleDictation,
    isRecording,
    isTranscribing,
  ]);

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      <AnimatePresence>
        {isPreviewMode && previewVersion && (
          <VersionPreviewBanner
            versionNumber={previewVersion.versionNumber}
            onRestore={restoreFromPreview}
            onClose={exitPreview}
            isRestoring={isRestoringPreview}
          />
        )}
      </AnimatePresence>

      <div ref={editorContainerRef} className="flex-grow pt-28 relative overflow-auto">
        {isMobile ? (
          <div
            className="w-full max-w-3xl mx-auto px-4 pb-96 macadamia-document-root"
            style={counterResetStyle}
          >
            {widgetsContent}
          </div>
        ) : (
          <A4PageWrapper isEditing={isEditing}>
            <div className="w-full macadamia-document-root" style={counterResetStyle}>
              {widgetsContent}
            </div>
          </A4PageWrapper>
        )}

        {isEditing && currentChapter?.sections.every((s: any) => s.blocks.length === 0) && (
          <AddWidgetButton onSelectWidget={addWidget} />
        )}
      </div>

      {/* Barre flottante mobile pour basculer editor / reader */}
      {isMobile && !isPreviewMode && ['author', 'co-author', 'admin'].includes(userRole || '') && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full border border-white/20 dark:border-white/10 shadow-lg bg-white/65 dark:bg-black/45"
          style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          <MacadamiaSwitcher />
        </div>
      )}

      {/* Gestionnaire de post-its */}
      <PostItManager
        dossierId={dossierId}
        currentChapterId={currentChapter?._id || ''}
        userRole={userRole}
        portalContainer={editorContainerRef}
      />
    </div>
  );
};

export default DossierEditorPane;
